import ReactGA from 'react-ga4';
import { matchPath } from 'react-router-dom';

const DYNAMIC_ROUTE_PATHS = [
  '/banho-tosa/agendamento/:data',
  '/clinica/agendamento/:data',
  '/clinica/check-in/order/:orderId',
  '/banho-tosa/check-in/order/:orderId',
  '/clientes/resumo/:id',
  '/cupom/editar/:id',
  '/pacotes/edit/:id',
  '/profissionais/editar/:id',
  '/servicos/editar/:edit',
  '/clinica/atendimento/:orderId',
];

const ANALYTICS_CONTEXT_KEYS = [
  'merchant_id',
  'merchant_name',
  'merchant_state',
  'department',
  'access_mode',
];

const USER_PROPERTY_VALUE_LIMIT = 36;

let initialized = false;
let lastPageLocation = null;
let lastAnalyticsContextSignature = null;
let currentAnalyticsContext = {};

const sanitizeAnalyticsValue = (value) => {
  if (value === null || value === undefined) return null;

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return null;

  return normalizedValue.slice(0, USER_PROPERTY_VALUE_LIMIT);
};

const normalizeAnalyticsContext = (context = {}) =>
  ANALYTICS_CONTEXT_KEYS.reduce((normalizedContext, key) => {
    normalizedContext[key] = sanitizeAnalyticsValue(context[key]);
    return normalizedContext;
  }, {});

const compactAnalyticsContext = (context = {}) =>
  ANALYTICS_CONTEXT_KEYS.reduce((compactContext, key) => {
    if (context[key] !== null && context[key] !== undefined) {
      compactContext[key] = context[key];
    }
    return compactContext;
  }, {});

const normalizeDepartment = (theme) => {
  const normalizedTheme = sanitizeAnalyticsValue(theme)?.toLowerCase();

  if (normalizedTheme === 'petland') return 'petland';
  if (normalizedTheme === 'dramei') return 'veterinary';

  return null;
};

export const buildMerchantAnalyticsContext = (
  merchant = {},
  { theme = null, accessMode = 'anonymous' } = {}
) => ({
  merchant_id: sanitizeAnalyticsValue(merchant?.id),
  merchant_name: sanitizeAnalyticsValue(merchant?.name?.storeName),
  merchant_state: sanitizeAnalyticsValue(merchant?.location?.uf)?.toUpperCase() || null,
  department: normalizeDepartment(theme),
  access_mode: sanitizeAnalyticsValue(accessMode),
});

export const normalizePagePath = (pathname = '/') => {
  const pathWithoutQueryOrHash = pathname.split(/[?#]/)[0];
  const normalizedPath = pathWithoutQueryOrHash.replace(/\/+$/, '') || '/';

  const dynamicRoute = DYNAMIC_ROUTE_PATHS.find((path) =>
    matchPath(normalizedPath, { path, exact: true })
  );

  return dynamicRoute || normalizedPath;
};

const sanitizeReferrer = (referrer) => {
  if (!referrer) return null;

  try {
    const url = new URL(referrer);
    return `${url.origin}${normalizePagePath(url.pathname)}`;
  } catch {
    return null;
  }
};

export const initGA = () => {
  if (!process.env.REACT_APP_GA_ID) return false;
  if (initialized) return true;

  ReactGA.initialize(process.env.REACT_APP_GA_ID, {
    gtagOptions: { send_page_view: false },
  });

  initialized = true;
  return true;
};

export const setAnalyticsContext = (context = {}) => {
  if (!initGA()) return false;

  const normalizedContext = normalizeAnalyticsContext(context);
  const contextSignature = JSON.stringify(normalizedContext);

  currentAnalyticsContext = normalizedContext;

  if (contextSignature === lastAnalyticsContextSignature) return true;

  ReactGA.gtag('set', 'user_properties', normalizedContext);
  lastAnalyticsContextSignature = contextSignature;

  return true;
};

export const logPageView = (pathname = window.location.pathname, context = null) => {
  if (context ? !setAnalyticsContext(context) : !initGA()) return;

  const pagePath = normalizePagePath(pathname);
  const pageLocation = `${window.location.origin}${pagePath}`;

  if (pageLocation === lastPageLocation) return;

  const pageReferrer = lastPageLocation || sanitizeReferrer(document.referrer);
  const pageView = {
    hitType: 'pageview',
    page: pagePath,
    page_location: pageLocation,
    page_title: document.title,
    ...compactAnalyticsContext(currentAnalyticsContext),
  };

  if (pageReferrer) pageView.page_referrer = pageReferrer;

  ReactGA.send(pageView);
  lastPageLocation = pageLocation;
};
